import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BookOpen, Quote, School, Star } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const BIO = [
  {
    n: "01",
    title: "Stories children can see themselves in",
    body: "Damali believes that children learn best when they are engaged, curious and having fun. Her stories encourage children to explore new experiences while gently introducing positive messages they can take with them beyond the final page — confidence, positive words and affirmations, oral health, healthy eating, starting school and becoming little explorers.",
  },
  {
    n: "02",
    title: "A purpose behind every story",
    body: "Whether a child is preparing for their first day at school, learning how to look after their teeth, discovering the importance of healthy choices or simply embarking on an adventure, Damali's aim is to make each experience feel exciting rather than overwhelming.",
  },
  {
    n: "03",
    title: "Only the beginning",
    body: "Through colourful storytelling and relatable characters, Damali hopes her books become part of those special childhood moments — the bedtime story, the classroom read, the book chosen again and again. Five books published is only the beginning: there are always more stories to imagine, more adventures to discover and more little minds to inspire.",
  },
];

const STATS = [
  { icon: BookOpen, label: "5 published picture books" },
  { icon: Star, label: "5.0 stars on Amazon UK" },
  { icon: School, label: "School & library visits" },
];

const POLAROIDS = [
  { src: "/images/author-reading-classroom.jpg", alt: "Damali reading a Bailey book to a classroom of children", caption: "Story time!", rotate: "-2deg" },
  { src: "/images/child-reading-bailey.jpeg", alt: "A young reader enjoying a Bailey book", caption: "Little readers", rotate: "1.6deg" },
  { src: "/images/child-books-table.jpeg", alt: "A child with the Bailey book collection", caption: "Book day joy", rotate: "-1.2deg" },
];

const FRIENDS = [
  { id: "explorer", name: "Explorer Bailey", img: "/images/explorer-bailey.png", motto: "Curious, brave, and ready to explore London!" },
  { id: "superhero", name: "Superhero Bailey", img: "/images/affirm-brave.png", motto: "I am brave — showing courage every day, in every way!" },
  { id: "thankful", name: "Thankful friend", img: "/images/affirm-thankful.png", motto: "I am thankful — feeling good for everything!" },
  { id: "happy", name: "Happy friend", img: "/images/affirm-happy.png", motto: "I am happy — feeling joy and showing it!" },
];

const ruledPaper = {
  backgroundImage:
    "repeating-linear-gradient(180deg, transparent 0, transparent 34px, rgba(79,163,227,0.10) 34px, rgba(79,163,227,0.10) 35px)",
};

const Tape = () => (
  <span
    aria-hidden="true"
    className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 rotate-[-3deg] rounded-sm opacity-80 pointer-events-none"
    style={{ background: "rgba(255,233,168,0.85)", boxShadow: "0 2px 5px rgba(49,67,92,0.15)" }}
  />
);

const FloatingCharacter = ({ src, alt, className, duration = 4, delay = 0 }) => (
  <motion.img
    src={src}
    alt={alt}
    aria-hidden="true"
    className={`pointer-events-none absolute drop-shadow-[0_14px_18px_rgba(49,67,92,0.3)] ${className}`}
    animate={{ y: [0, -12, 0], rotate: [0, 2, -2, 0] }}
    transition={{ repeat: Infinity, duration, delay, ease: "easeInOut" }}
  />
);

export default function AuthorPage() {
  const [friend, setFriend] = useState(FRIENDS[0]);

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24 overflow-x-clip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
            <div>
              <Eyebrow className="mb-5">Meet the author</Eyebrow>
              <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-walnut">
                <MaskedLine i={0}>Damali</MaskedLine>
                <MaskedLine i={1} className="text-terracotta">Allison</MaskedLine>
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="mt-6 max-w-xl font-display font-semibold text-lg sm:text-xl text-walnut leading-snug">
                  Creating stories that inspire little minds and big imaginations.
                </p>
                <p className="mt-4 max-w-xl text-base sm:text-lg text-walnutsoft leading-relaxed">
                  Damali Allison is a five-times published children's book author with a
                  passion for creating meaningful stories that children can connect with,
                  learn from and enjoy. For Damali, books have always been more than words
                  on a page they are places where children can imagine, discover, ask
                  questions, see themselves, and believe in what they can become.
                </p>
                <p className="mt-4 max-w-xl text-sm sm:text-base text-walnutsoft leading-relaxed">
                  Inspired by her own love of books and the joy that comes from discovering
                  a great story, Damali creates children's books that bring together
                  imagination, education and real-life experiences in a way that feels
                  natural and relatable to young readers.
                </p>
              </motion.div>
              <motion.div
                data-testid="author-stats-row"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.8 }}
                className="mt-7 flex flex-wrap gap-3"
              >
                {STATS.map((s) => (
                  <span
                    key={s.label}
                    className="inline-flex items-center gap-2 rounded-full bg-white/85 border border-sand px-4 py-2 text-xs sm:text-sm font-semibold text-walnut shadow-sm"
                  >
                    <s.icon size={15} className="text-amberbrand" /> {s.label}
                  </span>
                ))}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.8 }}
                className="mt-8 flex flex-wrap gap-4"
              >
                <Link
                  to="/books"
                  data-testid="author-explore-books"
                  className="group rounded-full bg-terracotta text-cream pl-7 pr-6 py-4 font-display font-bold text-sm sm:text-base flex items-center gap-2 hover:bg-terracottadark transition-colors"
                >
                  Explore the books <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="https://www.amazon.co.uk/Damali-Allison/e/B0DJ4H9CQ1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="author-amazon-follow"
                  className="rounded-full border-2 border-walnut text-walnut px-7 py-[0.85rem] font-display font-bold text-sm sm:text-base hover:bg-walnut hover:text-cream transition-colors"
                >
                  Follow on Amazon
                </a>
              </motion.div>
            </div>

            <motion.div
              data-testid="author-portrait-frame"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <FloatingCharacter src="/images/affirm-brave.png" alt="" className="w-16 sm:w-24 -right-2 -bottom-6 z-20" duration={5} delay={0.7} />
              <div className="spotlight rounded-[2rem] border border-sand p-8 sm:p-10 overflow-hidden">
                <img
                  src="/images/damali-author.webp"
                  alt="Damali Allison holding Bailey, What Can You Do? at a library author event"
                  className="w-full max-w-md mx-auto aspect-[4/5] object-cover rounded-2xl shadow-[0_30px_60px_-24px_rgba(44,34,30,0.4)]"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-walnut text-cream rounded-2xl px-6 py-4 shadow-xl rotate-[-2deg]">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta mb-1">5× published author</p>
                <p className="font-display font-bold text-lg leading-tight">5 books · 5.0 stars<br />on Amazon UK</p>
              </div>
            </motion.div>
          </div>

          <Reveal className="mb-28">
            <div data-testid="author-quote" className="relative bg-creamdark/60 border border-sand rounded-[2rem] p-10 sm:p-14 text-center max-w-4xl mx-auto overflow-hidden">
              <span aria-hidden="true" className="absolute inset-0" style={ruledPaper} />
              <div className="relative">
                <Quote size={36} className="mx-auto text-terracotta mb-6" />
                <p className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut leading-snug">
                  “Every child deserves to find a story that makes them feel inspired,
                  encouraged and proud of who they are.”
                </p>
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-walnutmuted">Damali Allison</p>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-28">
            <div className="lg:col-span-4">
              <Reveal className="lg:sticky lg:top-28">
                <Eyebrow className="mb-4">In her own words</Eyebrow>
                <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut mb-5">
                  A love of books. A passion for children.
                </h2>
                <p className="text-sm sm:text-base text-walnutsoft leading-relaxed">
                  Damali on why she writes, who she writes for — and the belief at the
                  heart of every Bailey story.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              {BIO.map((j, i) => (
                <Reveal key={j.n} delay={i * 0.06}>
                  <motion.div
                    data-testid={`journey-chapter-${j.n}`}
                    initial={{ rotateY: -14, opacity: 0, transformPerspective: 900 }}
                    whileInView={{ rotateY: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                    className="group relative grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-8 py-8 border-t border-sand last:border-b items-start hover:bg-white/50 transition-colors duration-300 rounded-xl px-3 -mx-3"
                  >
                    <span className="font-mono font-bold text-4xl sm:text-5xl text-terracotta/80 group-hover:text-terracotta transition-colors leading-none">
                      {j.n}
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-walnut mb-2">{j.title}</h3>
                      <p className="text-sm sm:text-base text-walnutsoft leading-relaxed max-w-xl">{j.body}</p>
                    </div>
                  </motion.div>
                </Reveal>
              ))}
              <Reveal delay={0.2}>
                <p className="pt-8 font-mono text-sm sm:text-base uppercase tracking-[0.3em] text-walnut">
                  Read · Imagine · Learn · Believe · <span className="text-terracotta">Explore</span>
                </p>
              </Reveal>
            </div>
          </div>

          <Reveal className="mb-28">
            <div data-testid="author-polaroid-wall">
              <Eyebrow className="mb-4 text-center">Classroom moments</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut text-center mb-12">
                Where the stories come alive
              </h2>
              <div className="flex flex-wrap justify-center gap-8 sm:gap-10">
                {POLAROIDS.map((p, i) => (
                  <motion.figure
                    key={p.src}
                    data-testid={`polaroid-${i + 1}`}
                    className="relative bg-white rounded-xl p-3 pb-12 shadow-[0_24px_44px_-18px_rgba(49,67,92,0.35)] w-64 sm:w-72"
                    style={{ rotate: p.rotate }}
                    initial={{ opacity: 0, y: 50, rotate: 0 }}
                    whileInView={{ opacity: 1, y: 0, rotate: p.rotate }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ type: "spring", stiffness: 80, damping: 12, delay: i * 0.12 }}
                    whileHover={{ scale: 1.05, rotate: 0, zIndex: 10 }}
                  >
                    <Tape />
                    <img src={p.src} alt={p.alt} loading="lazy" className="w-full aspect-[4/5] object-cover rounded-lg" />
                    <figcaption className="absolute bottom-3 inset-x-0 text-center font-display font-bold text-sm text-walnut">
                      {p.caption}
                    </figcaption>
                  </motion.figure>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal className="mb-28">
            <div data-testid="meet-friends" className="bg-sagecream border border-sand rounded-[2rem] p-8 sm:p-12">
              <Eyebrow className="mb-4 text-center">Meet Bailey & friends</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut text-center mb-3">
                Tap a friend to hear their motto
              </h2>
              <p className="text-sm sm:text-base text-walnutsoft text-center max-w-xl mx-auto mb-10">
                The characters little readers meet inside Damali's books — each one
                carries an affirmation to take beyond the final page.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-10">
                {FRIENDS.map((f) => (
                  <motion.button
                    key={f.id}
                    data-testid={`friend-${f.id}`}
                    onClick={() => setFriend(f)}
                    className={`relative rounded-3xl border-2 p-4 sm:p-5 w-32 sm:w-40 transition-colors cursor-pointer ${
                      friend.id === f.id ? "bg-white border-terracotta shadow-lg" : "bg-white/70 border-sand hover:border-terracotta/50"
                    }`}
                    whileHover={{ scale: 1.07, rotate: -1.5 }}
                    whileTap={{ scale: 0.94 }}
                  >
                    <img src={f.img} alt={f.name} className="w-full h-24 sm:h-32 object-contain" />
                    <span className="block mt-2 font-display font-bold text-xs sm:text-sm text-walnut">{f.name}</span>
                  </motion.button>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={friend.id}
                  data-testid="friend-motto"
                  className="max-w-md mx-auto bg-white/85 border border-sand rounded-3xl px-8 py-6 text-center shadow-md"
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 180, damping: 14 }}
                >
                  <p className="font-display font-extrabold text-xl sm:text-2xl text-walnut">“{friend.motto}”</p>
                  <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-amberbrand">{friend.name}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              <div className="rounded-[1.75rem] overflow-hidden border border-sand">
                <img
                  src="/images/author-reading-classroom.jpg"
                  alt="Damali Allison reading a Bailey book to a classroom of children"
                  loading="lazy"
                  className="w-full h-full min-h-[20rem] object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="bg-sage text-cream rounded-[1.75rem] p-8 sm:p-10 flex flex-col justify-between">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cream/70 mb-4">In the press & community</p>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-4">
                    Where you've seen Bailey
                  </h3>
                    <ul className="flex flex-col gap-3 text-sm sm:text-base text-cream/85 leading-relaxed">
                    <li className="flex gap-3"><BookOpen size={18} className="shrink-0 mt-1" /> Featured author page on Amazon UK — every title rated 5.0</li>
                    <li className="flex gap-3"><BookOpen size={18} className="shrink-0 mt-1" /> Published by Vision Books Publishing, an independent UK publisher championing new voices</li>
                    <li className="flex gap-3"><BookOpen size={18} className="shrink-0 mt-1" /> Regular author visits to primary schools and libraries across the UK</li>
                  </ul>
                </div>
                <Link
                  to="/books"
                  data-testid="author-press-cta"
                  className="mt-8 inline-flex items-center gap-2 font-display font-bold text-sm text-cream hover:gap-3.5 transition-all"
                >
                  See the books everyone's talking about <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
